import React, { useState, useEffect, useRef } from 'react';
import '@availity/yup';
import * as yup from 'yup';
import { Field, Form, Input } from '@availity/form';
import helpers from '../../../utils/helper';
import createNotification from '../../../utils/createNotification';
import {
    Col,
    Label,
    FormGroup,
    Button,
    ModalHeader,
    ModalBody,
    ModalFooter,
} from 'reactstrap';
import ButtonLoader from '../../../components/Layout/Buttons/ButtonLoader';
import Helpers from '../../../utils/helper';
import { COMMON_REGEX } from '../../../utils/constants';
import Rodal from '../../../components/Rodal/Rodal';
import { useStateSelector } from '../../../store/selectors';
import { createPortal } from 'react-dom';
import { RemoveTeamMemberModal } from './RemoveTeamMemberModal';
import { TeamMember } from '../../Admin/BuyerMarketplace/MyTeamMembers/Models/TeamMember';
import {
    ExternalPermissions,
    ExternalPermissionsOptionList,
    ExternalTeamMemberRole,
    ExternalTeamMemberRoleOptionList,
    InternalPermissions,
    InternalPermissionsOptionList,
    InternalTeamMemberRole,
    InternalTeamMemberRoleOptionList,
    TeamMemberType,
    TeamMemberTypeOptionList,
} from '../../Admin/BuyerMarketplace/MyTeamMembers/Models/Enums';
import './TeamMemberModal.scss';
import BootstrapTable from '../../../components/react-bootstrap-table-next/react-bootstrap-table2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrashCan, faSave } from '@fortawesome/free-solid-svg-icons';
import { AddAccessToOpportunitiesModal } from './AddAccessToOpportunitiesModal';
import { MarketplaceOpportunityStatusList } from '../../../slices/marketplace/opportunity/models/opportunity-status';
import { PracticeFocusOptionsMapping } from '../../../utils/constants/PracticeFocus.enum';

export enum DialogMode {
    Create = 1,
    Edit = 2,
    View = 3,
}

export const DialogModeOptionList = [
    { value: DialogMode.Create, label: 'Create' },
    { value: DialogMode.Edit, label: 'Edit' },
    { value: DialogMode.View, label: 'View' },
];

export interface TeamMemberModalProps {
    isTableMode: boolean;
    isVisible: boolean;
    dialogMode: DialogMode;
    data: TeamMember;
    currentUserId: number;
    setDialogMode: (mode: DialogMode) => void;
    setData: (data: any) => void;
    resetDialog: () => void;
    refreshTable: () => void;
    getSummaryData: () => Promise<any>;
    setOpportunityTableUpdateTrigger: React.Dispatch<
        React.SetStateAction<number>
    >;
    opportunityTableUpdateTrigger: number;
}

export interface TeamMemberOpportunityAccessRow {
    opportunityId: number;
    practiceName: string;
    practiceType: string;
    fteDvms: number;
    state: string;
    status: string;
    permissions: string;
}

export const TeamMemberModal = (props: TeamMemberModalProps) => {
    const modalRef = useRef(null);

    const [isDeleteDialogVisible, setIsDeleteDialogVisible] = useState(false);
    const [areNameAndEmailValid, setAreNameAndEmailValid] = useState(false);
    const [isSendingRequest, setIsSendingRequest] = useState(false);
    const [editingMappingIds, setEditingMappingIds] = useState<number[]>([]);
    const availableOpportunitiesForTeamMembers = useStateSelector(
        (state) => state.opportunities.availableOpportunitiesForTeamMembers
    );
    const axios = useStateSelector((state) => state.core.axios);
    const [
        isAddAccessToOpportunitiesVisible,
        setIsAddAccessToOpportunitiesVisible,
    ] = useState(false);

    const hideModal = (refreshTable = true) => {
        props.resetDialog();
        document.body.style.overflow = 'unset';
        if (modalRef?.current) {
            modalRef.current.resetForm();
        }
        if (refreshTable) {
            props.refreshTable();
        }
    };

    useEffect(() => {}, [props.data.associateMemberMarketplaceMappings]);

    useEffect(() => {
        if (props.isTableMode === false) {
            document.body.style.overflow = 'hidden';
        }
    }, [props.isTableMode]);

    const initSubmit = () => {
        modalRef.current.handleSubmit();
    };

    const remove = () => {
        setIsSendingRequest(true);
        axios
            .delete(`/api/Associate/${props.data.id}`)
            .then((response) => {
                if (Helpers.isResponseSuccessful(response)) {
                    createNotification(
                        'Team Member has been deleted successfully!',
                        'success'
                    );
                    setIsDeleteDialogVisible(false);
                    if (!props.isTableMode) {
                        props.getSummaryData().then(() => hideModal());
                    } else {
                        props.getSummaryData();
                        props.setDialogMode(null);
                    }
                }
            })
            .catch((error) => {
                createNotification(error.message, 'error');
            })
            .finally(() => {
                setIsSendingRequest(false);
            });
    };

    const submit = () => {
        if (props.dialogMode == DialogMode.Create) {
            create();
        } else {
            update();
        }
    };

    const create = () => {
        setIsSendingRequest(true);
        let resultObj = getRequestBody();
        resultObj.id = null;

        axios
            .post('/api/Associate', resultObj)
            .then((response) => {
                if (Helpers.isResponseSuccessful(response)) {
                    createNotification(
                        'Team Member has been successfully created!',
                        'success'
                    );
                    if (!props.isTableMode) {
                        props.getSummaryData();
                    }
                }
            })
            .catch((error) => {
                createNotification(error.message, 'error');
            })
            .finally(() => {
                hideModal();
                setIsSendingRequest(false);
            });
    };

    const update = () => {
        setIsSendingRequest(true);
        const resultObj = getRequestBody();
        axios
            .put('/api/Associate', resultObj)
            .then((response) => {
                if (Helpers.isResponseSuccessful(response)) {
                    createNotification(
                        'Team Member has been successfully updated!',
                        'success'
                    );
                    if (!props.isTableMode) {
                        props.getSummaryData().then(() => hideModal());
                    } else {
                        props.getSummaryData();
                        props.setDialogMode(DialogMode.View);
                    }
                }
            })
            .catch((error) => {
                createNotification(error.message, 'error');
            })
            .finally(() => {
                setIsSendingRequest(false);
            });
    };

    const getGeneralMapping = () => {
        return props.data.associateMemberMarketplaceMappings.find(
            (m: any) => m.isGeneralSettings
        );
    };

    const getRequestBody = () => {
        let obj = {
            id: props.data.id,
            name: props.data.name,
            email: props.data.email,
            ownerId: props.currentUserId,
            practices: [] as any[],
            associateMemberMarketplaceMappings:
                props.data.associateMemberMarketplaceMappings,
        };
        return obj;
    };

    const addAccessToOpportunities = (
        selectedIds: number[],
        defaultInternalPermission?: InternalPermissions
    ) => {
        const generalMapping = getGeneralMapping();
        const updatedMappings = props.data.associateMemberMarketplaceMappings;
        selectedIds.forEach((id) => {
            updatedMappings.push({
                id: Math.floor(Math.random() * 1000000000),
                type: generalMapping.type,
                internalRole: generalMapping.internalRole,
                externalRole: generalMapping.externalRole,
                internalPermissions:
                    generalMapping.internalPermissions ??
                    defaultInternalPermission,
                externalPermissions: generalMapping.externalPermissions,
                isGeneralSettings: false,
                opportunityId: id,
            });
        });
        props.setData({
            ...props.data,
            associateMemberMarketplaceMappings: updatedMappings,
        });
        return updatedMappings;
    };

    useEffect(() => {
        if (props.data) {
            setAreNameAndEmailValid(
                props.data.email &&
                    helpers.validateEmail(props.data.email) &&
                    props.data.name &&
                    props.data.name.trim() !== ''
            );
        }
    }, [props]);

    const memberModalHeader = (
        <ModalHeader>
            {props.dialogMode == DialogMode.Create
                ? 'Add Team Member'
                : props.dialogMode == DialogMode.Edit
                ? `Edit Team Member Access Rights: ${props.data.name}`
                : `Team Member Access Rights: ${props.data.name}`}
        </ModalHeader>
    );

    const getTeamMemberRole = () => {
        const generalMapping = getGeneralMapping();
        return generalMapping?.type === TeamMemberType.Internal
            ? generalMapping?.internalRole
            : generalMapping?.externalRole;
    };

    const getTeamMemberPermissions = () => {
        const generalMapping = getGeneralMapping();
        return generalMapping?.type === TeamMemberType.Internal
            ? generalMapping?.internalPermissions
            : generalMapping?.externalPermissions;
    };

    const renderMainInfoSection = () => {
        return (
            <div className="associate_form">
                <div className="row">
                    <Col md={4}>
                        <FormGroup>
                            {(props.dialogMode == DialogMode.Create ||
                                props.dialogMode == DialogMode.Edit) && (
                                <Field
                                    type="text"
                                    label="Name"
                                    name="name"
                                    disabled={
                                        props.dialogMode == DialogMode.Create
                                            ? false
                                            : true
                                    }
                                    placeholder={props.data.name}
                                    value={props.data.name}
                                    onChange={(event) => {
                                        props.data.name = event.target.value;
                                        props.setData({
                                            ...props.data,
                                        });
                                    }}
                                />
                            )}
                            {props.dialogMode == DialogMode.View && (
                                <div>
                                    <Label for="name">Name</Label>
                                    <p className="disabled-paragraph">
                                        {props.data.name}
                                    </p>
                                </div>
                            )}
                        </FormGroup>
                    </Col>

                    <Col md={4}>
                        <FormGroup>
                            {(props.dialogMode == DialogMode.Create ||
                                props.dialogMode == DialogMode.Edit) && (
                                <Field
                                    type="email"
                                    name="email"
                                    id="email"
                                    label="Email"
                                    placeholder={props.data.email}
                                    value={props.data.email}
                                    disabled={
                                        props.dialogMode == DialogMode.Create
                                            ? false
                                            : true
                                    }
                                    onChange={(event) => {
                                        props.data.email = event.target.value;
                                        props.setData({
                                            ...props.data,
                                        });
                                    }}
                                />
                            )}
                            {props.dialogMode == DialogMode.View && (
                                <div>
                                    <Label for="email">Email</Label>
                                    <p className="disabled-paragraph">
                                        {props.data.email}
                                    </p>
                                </div>
                            )}
                        </FormGroup>
                    </Col>

                    <Col md={4}>
                        <FormGroup>
                            {props.dialogMode == DialogMode.Edit && (
                                <Field
                                    type="text"
                                    name="status"
                                    id="status"
                                    placeholder={props.data.status}
                                    value={props.data.status}
                                    label="Status"
                                    disabled={true}
                                />
                            )}

                            {props.dialogMode == DialogMode.View && (
                                <div>
                                    <Label for="status">Status</Label>
                                    <p className="disabled-paragraph">
                                        {props.data.status}
                                    </p>
                                </div>
                            )}
                        </FormGroup>
                    </Col>
                </div>
            </div>
        );
    };

    const getOpportuntityTableColumns = () => [
        { dataField: 'opportunityId', text: 'Opp Id', sort: false },
        { dataField: 'practiceName', text: 'Practice Name', sort: false },
        { dataField: 'practiceType', text: 'Service Focus', sort: false },
        { dataField: 'fteDvms', text: 'FTE DVMs', sort: false },
        { dataField: 'state', text: 'State', sort: false },
        { dataField: 'status', text: 'Status', sort: false },
        {
            dataField: 'permissions',
            text: 'Permissions',
            sort: false,
            formatter: (_: any, row: any) => {
                const mapping =
                    props.data.associateMemberMarketplaceMappings.find(
                        (m: any) =>
                            m.opportunityId === row.opportunityId &&
                            !m.isGeneralSettings
                    );
                if (!mapping) return row.permissions;
                const isEditing = editingMappingIds.includes(mapping.id);
                const isInternal = mapping.type === TeamMemberType.Internal;
                const options = isInternal
                    ? InternalPermissionsOptionList
                    : ExternalPermissionsOptionList;
                if (!isEditing) {
                    return row.permissions;
                }
                return (
                    <select
                        className="change-permissions-dropdown"
                        value={
                            isInternal
                                ? mapping.internalPermissions
                                : mapping.externalPermissions
                        }
                        onChange={(e) =>
                            handlePermissionChange(
                                mapping.id,
                                Number(e.target.value)
                            )
                        }>
                        <option value="">Select Permissions</option>
                        {options.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                );
            },
        },
        {
            dataField: 'id',
            hidden: props.dialogMode === DialogMode.View,
            text: 'Actions',
            isDummyField: true,
            formatter: (_cell: any, row: any) => {
                const mapping =
                    props.data.associateMemberMarketplaceMappings.find(
                        (m: any) =>
                            m.opportunityId === row.opportunityId &&
                            !m.isGeneralSettings
                    );
                if (!mapping) return null;
                const isEditing = editingMappingIds.includes(mapping.id);
                return (
                    <div className="action-icons">
                        {!isEditing ? (
                            <FontAwesomeIcon
                                onClick={() => handleEditMapping(mapping.id)}
                                icon={faEdit}
                                style={{ cursor: 'pointer' }}
                            />
                        ) : (
                            <FontAwesomeIcon
                                onClick={() => handleSaveMapping(mapping.id)}
                                icon={faSave}
                                style={{ cursor: 'pointer' }}
                            />
                        )}
                        <FontAwesomeIcon
                            className="delete-button"
                            onClick={() => {
                                removeOpportunity(row.opportunityId);
                            }}
                            icon={faTrashCan}
                            style={{ cursor: 'pointer', marginLeft: 8 }}
                        />
                    </div>
                );
            },
            headerAlign: 'center',
            align: 'center',
        },
    ];

    const removeOpportunity = (opportunityId: number) => {
        const updatedMappings =
            props.data.associateMemberMarketplaceMappings.filter(
                (x) => x.opportunityId !== opportunityId
            );

        props.setData({
            ...props.data,
            associateMemberMarketplaceMappings: updatedMappings,
        });
    };

    const getCurrentOpportynitiesAccessTableData = () => {
        const oppToMap = props.data.associateMemberMarketplaceMappings.filter(
            (x) =>
                !x.isGeneralSettings &&
                availableOpportunitiesForTeamMembers.some(
                    (o) => o.opportunityId === x.opportunityId
                )
        );

        let result = oppToMap.map((x) => {
            const currentOpp = availableOpportunitiesForTeamMembers.find(
                (o) => o.opportunityId == x.opportunityId
            );
            const practiceNameLabel = currentOpp.practices
                .map((p) => {
                    if (p.practiceName) {
                        return p.practiceName;
                    }
                    return '';
                })
                .join(', ');

            return {
                opportunityId: x.opportunityId,
                practiceName: practiceNameLabel,
                practiceType: currentOpp.practices
                    .map(
                        (p) =>
                            PracticeFocusOptionsMapping.find(
                                (y) => y.id === p.practiceType
                            )?.fullName
                    )
                    .join(', '),
                fteDvms: currentOpp.practices
                    .map((p) => p.fteDvms)
                    .reduce((acc, val) => acc + val, 0),
                state: currentOpp.practices
                    .map((p) => p.stateOfRegistration)
                    .join(', '),
                status: MarketplaceOpportunityStatusList.find(
                    (x) => x.id === currentOpp.status
                )?.name,
                permissions:
                    x.type === TeamMemberType.Internal
                        ? InternalPermissionsOptionList.find(
                              (z) => z.value === x.internalPermissions
                          )?.label
                        : ExternalPermissionsOptionList.find(
                              (z) => z.value === x.externalPermissions
                          )?.label,
            } as TeamMemberOpportunityAccessRow;
        });

        return result;
    };

    const renderOpportunityTableSection = () => {
        return (
            <div className="opportunity-table-section">
                <h6>Opportunity Assignments</h6>
                <div className="table-responsive">
                    <BootstrapTable
                        bootstrap4
                        key={props.opportunityTableUpdateTrigger}
                        keyField="id"
                        data={getCurrentOpportynitiesAccessTableData()}
                        columns={getOpportuntityTableColumns()}
                        noDataIndication={'No Data available'}></BootstrapTable>
                </div>
                {props.dialogMode != DialogMode.View &&
                    isGeneralPermissionSelected() && (
                        <div className="mt-2">
                            <Button
                                color="primary"
                                className="small-btn"
                                onClick={() =>
                                    setIsAddAccessToOpportunitiesVisible(true)
                                }>
                                Assign to New Opportunity
                            </Button>
                        </div>
                    )}
            </div>
        );
    };

    const addAccessToAllOpportunitiesIfRoleIsCorpDev = (role: number) => {
        const generalMapping = getGeneralMapping();
        let updatedMappings = props.data.associateMemberMarketplaceMappings;
        if (
            generalMapping.type === TeamMemberType.Internal &&
            role === InternalTeamMemberRole.CorpDev
        ) {
            let opportunityIds = availableOpportunitiesForTeamMembers.map(
                (x) => x.opportunityId
            );
            let opportunitiesToAdd = opportunityIds.filter(
                (x) =>
                    !props.data.associateMemberMarketplaceMappings.some(
                        (m: any) => m.opportunityId === x
                    )
            );
            updatedMappings = addAccessToOpportunities(
                opportunitiesToAdd,
                InternalPermissions.ReadOnly
            );
            props.setOpportunityTableUpdateTrigger((prev: number) => prev + 1);
        }

        const result = updatedMappings.map((m: any) => {
            return {
                ...m,
                internalRole: m.type == TeamMemberType.Internal ? role : null,
                externalRole: m.type == TeamMemberType.External ? role : null,
            };
        });
        props.setData({
            ...props.data,
            associateMemberMarketplaceMappings: result,
        });
    };

    const isGeneralPermissionSelected = () => {
        const generalMapping = getGeneralMapping();
        if (!generalMapping || !generalMapping.type) return false;
        return generalMapping.type === TeamMemberType.Internal
            ? generalMapping.internalPermissions != null
            : generalMapping.externalPermissions != null;
    };

    const resetAccessRigthsByRoleChange = (teamMemberType: TeamMemberType) => {
        if (modalRef.current) {
            modalRef.current.setFieldValue(
                'teamMemberType',
                `${teamMemberType}`
            );
            modalRef.current.setFieldValue('teamMemberRole', '1');
            modalRef.current.setFieldValue('permissions', '1');
        }
        let updatedMappings = props.data.associateMemberMarketplaceMappings;
        if (teamMemberType === TeamMemberType.Internal) {
            let opportunityIds = availableOpportunitiesForTeamMembers.map(
                (x) => x.opportunityId
            );
            let opportunitiesToAdd = opportunityIds.filter(
                (x) =>
                    !props.data.associateMemberMarketplaceMappings.some(
                        (m: any) => m.opportunityId === x
                    )
            );
            updatedMappings = addAccessToOpportunities(
                opportunitiesToAdd,
                InternalPermissions.ReadOnly
            );
        }
        updatedMappings = props.data.associateMemberMarketplaceMappings.map(
            (m: any) => {
                if (teamMemberType === TeamMemberType.Internal) {
                    return {
                        ...m,
                        type: TeamMemberType.Internal,
                        internalRole: InternalTeamMemberRole.CorpDev,
                        internalPermissions: InternalPermissions.ReadOnly,
                        externalRole: null,
                        externalPermissions: null,
                    };
                } else {
                    return {
                        ...m,
                        type: TeamMemberType.External,
                        externalRole: ExternalTeamMemberRole.Qoe,
                        externalPermissions: ExternalPermissions.Vdr,
                        internalRole: null,
                        internalPermissions: null,
                    };
                }
            }
        );

        props.setData({
            ...props.data,
            associateMemberMarketplaceMappings: updatedMappings,
        });

        props.setOpportunityTableUpdateTrigger((prev) => prev + 1);
    };

    const renderPermissioningSection = () => {
        const generalMapping = getGeneralMapping();
        const isInternal = generalMapping?.type === TeamMemberType.Internal;
        return (
            <>
                <div className="feature_checkbox practice-detail">
                    <h6 className="associate-popup">Permissioning</h6>
                </div>
                <FormGroup className="dropdown-list">
                    <div className="role-dropdown">
                        <label>Type</label>
                        <div className="customerStyle">
                            <Input
                                type="select"
                                name="teamMemberType"
                                value={generalMapping?.type || ''}
                                disabled={props.dialogMode === DialogMode.View}
                                onChange={(e) => {
                                    const teamMemberType = Number(
                                        e.target.value
                                    );

                                    resetAccessRigthsByRoleChange(
                                        teamMemberType
                                    );
                                }}>
                                <option value="">Select Type</option>
                                {TeamMemberTypeOptionList.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </Input>
                        </div>
                    </div>
                    <div className="role-dropdown">
                        <label>Role</label>
                        <div className="customerStyle">
                            <Input
                                type="select"
                                name="teamMemberRole"
                                value={getTeamMemberRole()}
                                disabled={
                                    props.dialogMode === DialogMode.View ||
                                    !generalMapping?.type
                                }
                                onChange={(e) => {
                                    const teamMemberRole = Number(
                                        e.target.value
                                    );

                                    addAccessToAllOpportunitiesIfRoleIsCorpDev(
                                        teamMemberRole
                                    );
                                }}>
                                <option value="">Select Role</option>
                                {(isInternal
                                    ? InternalTeamMemberRoleOptionList.filter(
                                          (role) =>
                                              role.value !==
                                              InternalTeamMemberRole.PrimaryMember
                                      )
                                    : ExternalTeamMemberRoleOptionList
                                ).map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </Input>
                        </div>
                    </div>
                    <div className="role-dropdown">
                        <label>Default Permission(s)</label>
                        <div className="customerStyle">
                            <Input
                                type="select"
                                name="permissions"
                                value={getTeamMemberPermissions()}
                                disabled={
                                    props.dialogMode === DialogMode.View ||
                                    !generalMapping?.type
                                }
                                onChange={(e) => {
                                    const permissions = Number(e.target.value);
                                    const updatedMappings =
                                        props.data.associateMemberMarketplaceMappings.map(
                                            (m: any) =>
                                                m.isGeneralSettings
                                                    ? {
                                                          ...m,
                                                          internalPermissions:
                                                              m.type ==
                                                              TeamMemberType.Internal
                                                                  ? permissions
                                                                  : null,
                                                          externalPermissions:
                                                              m.type ==
                                                              TeamMemberType.External
                                                                  ? permissions
                                                                  : null,
                                                      }
                                                    : m
                                        );
                                    props.setData({
                                        ...props.data,
                                        associateMemberMarketplaceMappings:
                                            updatedMappings,
                                    });
                                }}>
                                <option value="">Select Permissions</option>
                                {(isInternal
                                    ? InternalPermissionsOptionList
                                    : ExternalPermissionsOptionList
                                ).map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </Input>
                        </div>
                    </div>
                </FormGroup>
            </>
        );
    };

    const memberModalBody = (
        <>
            {props.data ? (
                <ModalBody>
                    <Form
                        innerRef={modalRef}
                        onSubmit={() => {
                            submit();
                        }}
                        initialValues={{
                            name: props.data.name,
                            email: props.data.email,
                            status: props.data.status,
                            teamMemberType: getGeneralMapping()?.type,
                            teamMemberRole: getTeamMemberRole(),
                            permissions: getTeamMemberPermissions(),
                        }}
                        validationSchema={yup.object().shape({
                            name: yup
                                .string()
                                .required('Name is required')
                                .min(1, 'Name is required'),
                            email: yup
                                .string()
                                .required('Email is required')
                                .matches(
                                    COMMON_REGEX.EMAIL,
                                    'Email is invalid'
                                ),
                            teamMemberRole: yup
                                .number()
                                .required('Role is required'),
                            teamMemberType: yup
                                .number()
                                .required('Type is required'),
                            permissions: yup
                                .number()
                                .required('Permission is required'),
                        })}>
                        {props.dialogMode == DialogMode.Create && (
                            <div>
                                <p>
                                    The primary member of a Buyer may invite
                                    other team members, either internal or
                                    external to their organization, to create a
                                    secure account on the VV Connect Platform.
                                    Along with the invitation, the primary
                                    member can establish the nature of that
                                    invited member's role and access on the
                                    platform, as seen below. Any of this is
                                    editable at anytime by the primary member.
                                </p>

                                <h6>Invite New Team Member</h6>
                            </div>
                        )}
                        {renderMainInfoSection()}
                        {renderPermissioningSection()}
                        {renderOpportunityTableSection()}
                    </Form>
                </ModalBody>
            ) : (
                []
            )}
        </>
    );

    const memberModalFooter = (
        <ModalFooter>
            <div className="footer-wrapper">
                <div className="edit-btn">
                    {props.dialogMode == DialogMode.View && (
                        <Button
                            className="edit"
                            onClick={() => {
                                props.setDialogMode(DialogMode.Edit);
                                modalRef.current.values.name = props.data.name;
                                modalRef.current.values.email =
                                    props.data.email;
                                modalRef.current.values.status =
                                    props.data.status;
                            }}
                            color="primary">
                            Edit
                        </Button>
                    )}
                </div>
                <div className="remove-cancel-btn member-button-panel">
                    {!props.isTableMode && (
                        <Button
                            className="cancel"
                            onClick={() => {
                                hideModal(false);
                            }}>
                            Cancel
                        </Button>
                    )}
                    {props.isTableMode &&
                        props.dialogMode == DialogMode.Edit && (
                            <Button
                                className="cancel"
                                onClick={() =>
                                    props.setDialogMode(DialogMode.View)
                                }>
                                Cancel
                            </Button>
                        )}
                    {props.dialogMode == DialogMode.Create && (
                        <ButtonLoader
                            buttonText={'Submit Request'}
                            loaderButtonText={'Submit Request'}
                            disabled={false}
                            isLoading={isSendingRequest}
                            className="btn btn-primary loader-submit"
                            onClick={initSubmit}></ButtonLoader>
                    )}
                    {props.dialogMode == DialogMode.Edit && (
                        <ButtonLoader
                            buttonText={'Save'}
                            loaderButtonText={'Save'}
                            disabled={false}
                            isLoading={isSendingRequest}
                            onClick={initSubmit}
                            className={
                                isSendingRequest
                                    ? 'btn btn-primary loader-submit'
                                    : 'btn btn-primary loader-submit save'
                            }></ButtonLoader>
                    )}
                    {props.dialogMode == DialogMode.View && (
                        <Button
                            onClick={() => setIsDeleteDialogVisible(true)}
                            className="remove"
                            color="primary">
                            Remove
                        </Button>
                    )}
                </div>
            </div>
        </ModalFooter>
    );

    const popupMode = createPortal(
        <div className={'associate-members-popup team-member-modal'}>
            {props.data && (
                <Rodal
                    visible={props.isVisible}
                    onClose={() => hideModal(false)}
                    animation={'fade'}
                    showMask={true}
                    width={900}>
                    {memberModalHeader}
                    {memberModalBody}
                    {memberModalFooter}
                </Rodal>
            )}
        </div>,
        document.body,
        'associate-member-popup-portal'
    );

    const tableMode = (
        <>
            {props.data && (
                <div className="modal-style top membership_profile_popup">
                    {memberModalHeader}
                    {memberModalBody}
                    {memberModalFooter}
                </div>
            )}
        </>
    );

    const getAvailableOpportunitiesToAddAccess = () => {
        return availableOpportunitiesForTeamMembers.filter((opportunity) => {
            return !props.data.associateMemberMarketplaceMappings.some(
                (x) => x.opportunityId === opportunity.opportunityId
            );
        });
    };

    const handleEditMapping = (mappingId: number) => {
        setEditingMappingIds((prev) => [...prev, mappingId]);
        props.setOpportunityTableUpdateTrigger((prev) => prev + 1);
    };

    const handlePermissionChange = (mappingId: number, value: number) => {
        const updatedMappings =
            props.data.associateMemberMarketplaceMappings.map((m: any) => {
                if (m.id === mappingId) {
                    if (m.type === TeamMemberType.Internal) {
                        return { ...m, internalPermissions: value };
                    } else {
                        return { ...m, externalPermissions: value };
                    }
                }
                return m;
            });
        props.setData({
            ...props.data,
            associateMemberMarketplaceMappings: updatedMappings,
        });
    };

    const handleSaveMapping = (mappingId: number) => {
        setEditingMappingIds((prev) => prev.filter((id) => id !== mappingId));
        props.setOpportunityTableUpdateTrigger((prev) => prev + 1);
    };

    return (
        <div className="team-member-modal">
            {props.isTableMode ? tableMode : popupMode}
            {props.data && (
                <div
                    className={
                        props.dialogMode == DialogMode.Create
                            ? 'create-dialog'
                            : 'edit-dialog'
                    }>
                    {isDeleteDialogVisible && (
                        <RemoveTeamMemberModal
                            isVisible={isDeleteDialogVisible}
                            dialogMode={props.dialogMode}
                            isSendingRequest={isSendingRequest}
                            setDialogMode={props.setDialogMode}
                            setIsDeleteDialogVisible={setIsDeleteDialogVisible}
                            remove={remove}></RemoveTeamMemberModal>
                    )}
                </div>
            )}
            {isAddAccessToOpportunitiesVisible && (
                <AddAccessToOpportunitiesModal
                    AddAccessToOpportunities={addAccessToOpportunities}
                    isVisible={isAddAccessToOpportunitiesVisible}
                    setIsVisible={setIsAddAccessToOpportunitiesVisible}
                    opportunities={getAvailableOpportunitiesToAddAccess()}
                />
            )}
        </div>
    );
};
